import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import { attachDiagramTo } from "../../../actions";
import { brand } from "../../../theme/businessFlowsTheme";
import { detectFile, formatSize, MAX_FILE_BYTES, readFile, readSample } from "./fileFormats";

// One drop zone for any file. The format is read from the file itself: a
// diagram the app draws keeps its shape, anything else is kept as a file to
// download. Too large, or a program or web page, is said before uploading.
export default function FileUpload({ database, guid, onAttached, onError }) {
  // The file and what it was detected as, set together so neither shows alone.
  const [picked, setPicked] = useState({ file: null, shape: null });
  const { file, shape } = picked;
  const [busy, setBusy] = useState(false);

  const choose = async (next) => {
    onError("");
    if (!next) return;
    const sample = /\.xml$/i.test(next.name) ? await readSample(next) : "";
    setPicked({ file: next, shape: detectFile(next, sample) });
  };

  const dropzone = useDropzone({ multiple: false, maxFiles: 1, onDrop: (accepted) => choose(accepted[0]) });

  const attach = async () => {
    if (!file || !shape || shape.error) return;
    setBusy(true);
    onError("");
    try {
      const content = await readFile(file, shape.encoding);
      const res = await attachDiagramTo(database, guid, shape.format, content, file.name, { fileName: file.name, mimeType: shape.mimeType });
      setBusy(false);
      if (res?.success) {
        setPicked({ file: null, shape: null });
        onAttached(`${file.name} attached as ${shape.label}. Any earlier ${shape.format.toUpperCase()} is kept as history.`);
      } else onError(res?.msg || "Couldn't attach the file.");
    } catch (err) {
      setBusy(false);
      onError(err.message || "Couldn't read the file.");
    }
  };

  return (
    <>
      <Box
        {...dropzone.getRootProps()}
        data-testid="file-drop"
        sx={{
          mt: 2,
          p: 3,
          borderRadius: 4,
          border: `2px dashed ${dropzone.isDragActive ? brand.ink : brand.line}`,
          bgcolor: dropzone.isDragActive ? brand.markSoft : brand.canvas,
          textAlign: "center",
          cursor: "pointer",
        }}
      >
        <input {...dropzone.getInputProps()} aria-label="Choose a file to attach" />
        {file ? (
          <Stack spacing={0.25} alignItems="center">
            <InsertDriveFileRoundedIcon sx={{ color: brand.inkSoft }} />
            <Typography variant="body2" sx={{ fontWeight: 650, overflowWrap: "anywhere" }}>
              {file.name}
            </Typography>
            <Typography variant="caption" color={shape?.error ? "error" : "text.secondary"}>
              {shape?.error
                ? shape.error
                : `${shape.label} · ${formatSize(file.size)}${shape.download ? " · readers download it" : ""} · click to choose another`}
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={0.25} alignItems="center">
            <CloudUploadRoundedIcon sx={{ color: brand.inkSoft }} />
            <Typography variant="body2" sx={{ fontWeight: 650 }}>
              Drop any file here, or click to choose
            </Typography>
            <Typography variant="caption" color="text.secondary">
              BPMN, SVG, images, PDF and Mermaid show in the viewer; any other file (Word, Excel, text, zip, …) is kept to download. Up to{" "}
              {formatSize(MAX_FILE_BYTES)}.
            </Typography>
          </Stack>
        )}
      </Box>
      {busy && <LinearProgress sx={{ mt: 1, borderRadius: 999 }} />}
      <Button
        variant="contained"
        sx={{ mt: 1.5 }}
        startIcon={<CloudUploadRoundedIcon />}
        disabled={!file || !shape || Boolean(shape.error) || busy}
        onClick={attach}
      >
        Attach file
      </Button>
    </>
  );
}
